// Feed rank context carried on `job_card_clicked`, `job_apply_outbound_clicked`
// and `job_detail_rendered`.
//
// PostHog audit 2026-10-21 (28d): 40% of `job_card_clicked` and 557 of 611
// apply events carried no rank, score or algo metadata, so CTR-by-position and
// apply-by-algorithm were unmeasurable for most of the funnel. Two reasons:
// only the feed card sent `relevance_score`/`algo_version`, and those were
// undefined on the default SQL feed path; and an apply from the job-detail
// page (the bulk of applies) had no way to know which list position the
// visitor clicked to get there.
//
// This module fixes both: `JobRankContext` is one fixed set of keys every one
// of the three events carries (null when unknown, never absent), and the
// list click stashes its context in sessionStorage keyed by job id so the
// detail page and its apply CTA can attribute back to it. sessionStorage, not a
// cookie: nothing here may emit `Set-Cookie` on a cacheable surface.

import type { JobRankContext } from "./events";

export const RANK_CONTEXT_KEYS = [
	"position",
	"list_surface",
	"score",
	"algo_version",
	"source_id",
	"indonesia_eligibility",
] as const satisfies readonly (keyof JobRankContext)[];

export const EMPTY_RANK_CONTEXT: JobRankContext = {
	position: null,
	list_surface: null,
	score: null,
	algo_version: null,
	source_id: null,
	indonesia_eligibility: null,
};

/**
 * Ranking variant that produced a server-rendered feed page. Same three
 * values `FeedImpressionTracker` has always been given, so impression, click
 * and apply rows join on one vocabulary. A row's own `algoVersion` (set by the
 * diversity-rerank arm) wins over this page-level default.
 */
export function feedAlgoVersion(opts: {
	isPersonalized: boolean;
	isColdStartRanked: boolean;
}): string {
	if (opts.isPersonalized) return "personalized_v2";
	if (opts.isColdStartRanked) return "cold_start";
	return "chronological";
}

/** Build the rank context for one rendered job card. */
export function buildJobRankContext(input: {
	position?: number | null;
	listSurface?: string | null;
	score?: number | null;
	algoVersion?: string | null;
	sourceSlug?: string | null;
	geoFit?: string | null;
}): JobRankContext {
	const position =
		typeof input.position === "number" && Number.isFinite(input.position)
			? input.position
			: null;
	const score =
		typeof input.score === "number" && Number.isFinite(input.score)
			? input.score
			: null;
	return {
		position,
		list_surface: input.listSurface || null,
		score,
		algo_version: input.algoVersion || null,
		source_id: input.sourceSlug || null,
		// Stored `jobs.geo_fit`. Absent is reported as null, not "unknown", so a
		// surface that never had the column is distinguishable from a job whose
		// verdict really is unknown.
		indonesia_eligibility: input.geoFit || null,
	};
}

const STORAGE_KEY = "ld:job-rank-context:v1";
/** Bound the stash: a session that clicks 200 jobs keeps only the newest. */
const MAX_ENTRIES = 50;

type Stash = Record<string, JobRankContext & { at: number }>;

function readStash(): Stash {
	try {
		if (typeof window === "undefined") return {};
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		return parsed && typeof parsed === "object" ? (parsed as Stash) : {};
	} catch {
		return {};
	}
}

/**
 * Remember the list context a job was opened from, so the detail page and its
 * apply CTA can report it. Fields that are null in `ctx` never erase a
 * non-null value already stashed (the detail page adds `source_id` /
 * `indonesia_eligibility` without wiping the feed's `position`).
 */
export function rememberRankContext(
	jobId: string,
	ctx: Partial<JobRankContext>,
): void {
	try {
		if (typeof window === "undefined" || !jobId) return;
		const stash = readStash();
		const prev = stash[jobId];
		const next: JobRankContext & { at: number } = {
			...EMPTY_RANK_CONTEXT,
			...(prev ?? {}),
			at: Date.now(),
		};
		for (const key of RANK_CONTEXT_KEYS) {
			const v = ctx[key];
			if (v !== null && v !== undefined) {
				(next as Record<string, unknown>)[key] = v;
			}
		}
		stash[jobId] = next;
		const ids = Object.keys(stash);
		if (ids.length > MAX_ENTRIES) {
			ids
				.sort((a, b) => (stash[a]?.at ?? 0) - (stash[b]?.at ?? 0))
				.slice(0, ids.length - MAX_ENTRIES)
				.forEach((id) => {
					delete stash[id];
				});
		}
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stash));
	} catch {
		// Storage blocked (private mode, quota) — attribution degrades to null,
		// the click itself must never break.
	}
}

export function recallRankContext(jobId: string | null): JobRankContext | null {
	if (!jobId) return null;
	const hit = readStash()[jobId];
	if (!hit) return null;
	const out: JobRankContext = { ...EMPTY_RANK_CONTEXT };
	for (const key of RANK_CONTEXT_KEYS) {
		const v = hit[key];
		if (v !== undefined) (out as Record<string, unknown>)[key] = v;
	}
	return out;
}

/**
 * Resolve the full rank context for an event: an explicit value on the event
 * wins, then the value stashed by the list click, then null. Every key is
 * always present in the result.
 */
export function resolveRankContext(
	jobId: string | null,
	explicit: Partial<Record<keyof JobRankContext, unknown>>,
): JobRankContext {
	const recalled = recallRankContext(jobId);
	const out: JobRankContext = { ...EMPTY_RANK_CONTEXT };
	for (const key of RANK_CONTEXT_KEYS) {
		const v = explicit[key];
		(out as Record<string, unknown>)[key] =
			v !== undefined && v !== null ? v : (recalled?.[key] ?? null);
	}
	return out;
}
